import apiClient from '../index';

export const spApi = {
  // Get all service providers
  getProviders: async (page = 1, searchQuery = '') => {
    try {
      const params = { page };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await apiClient.get('/service-providers', { params });
      
      const responseData = response?.data || response;
      let data = [];
      if (Array.isArray(responseData?.data)) {
        data = responseData.data; // Paginated wrapping (e.g. { data: [...], current_page: ... })
      } else if (Array.isArray(responseData)) {
        data = responseData; // Flat array fallback
      } else if (responseData?.service_providers && Array.isArray(responseData.service_providers)) {
        data = responseData.service_providers;
      }
      
      const providers = data.filter(Boolean).map(provider => ({
        id: provider.id,
        name: provider.name,
        mobile: provider.mobile,
        email: provider.email,
        website: provider.website,
        description: provider.description,
        location: provider.location_name,
        status: provider.status,
        facebook: provider.facebook,
        instagram: provider.instagram,
        twitter: provider.twitter,
        youtube: provider.youtube,
        wallpaper: provider.wallpaper,
        categories: provider.categories,
        category_ids: provider.categories?.map(cat => cat?.id).filter(Boolean) || []
      }));

      return {
        providers,
        pagination: {
          current_page: responseData?.current_page || 1,
          per_page: responseData?.per_page || 25,
          total_records: responseData?.total_records || providers.length,
          total_pages: responseData?.total_pages || 1,
        }
      };
    } catch (error) {
      console.error('Error fetching service providers:', error);
      return { providers: [], pagination: { current_page: 1, total_pages: 1 } };
    }
  },

  // Get single service provider by ID
  getProviderById: (id) => apiClient.get(`/service_providers/${id}`),

  // Get public details for a service provider (used on details page)
  getProviderPublic: async (id) => {
    try {
      const response = await apiClient.get(`/service-provider/public/${id}`);
      return response?.data || response;
    } catch (error) {
      console.error(`Error fetching public provider details for ${id}:`, error);
      return null;
    }
  },

  // Get providers by category ID (legacy endpoint)
  getProvidersByCategory: async (categoryId) => {
    try {
      const response = await apiClient.get(`/sps_by_category/${categoryId}`);
      let data = [];
      if (Array.isArray(response?.data?.data)) data = response.data.data;
      else if (Array.isArray(response?.data)) data = response.data;
      else if (Array.isArray(response)) data = response;
      else if (response?.providers && Array.isArray(response.providers)) data = response.providers;
      else if (response?.providers?.data && Array.isArray(response.providers.data)) data = response.providers.data;

      return data.filter(Boolean).map(provider => ({
          id: provider.id,
          name: provider.name,
          mobile: provider.mobile,
          email: provider.email,
          website: provider.website,
          description: provider.description,
          location: provider.location,
          status: provider.status,
          facebook: provider.facebook,
          instagram: provider.instagram,
          twitter: provider.twitter,
          youtube: provider.youtube,
          wallpaper: provider.wallpaper,
          categories: provider.categories,
          category_ids: provider.categories?.map(cat => cat?.id).filter(Boolean) || []
      }));
    } catch (error) {
      console.error(`Error fetching providers for category ${categoryId}:`, error);
      return [];
    }
  },

  // Get category details + service providers list (with search support)
  getCategoryDetails: async (categoryId, searchQuery = '', page = 1) => {
    try {
      const params = { page };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await apiClient.get(`/service-category/${categoryId}`, { params });
      
      const responseData = response?.data || response;
      const category = responseData?.category || null;
      
      let providers = [];
      if (Array.isArray(responseData?.service_providers)) {
        providers = responseData.service_providers;
      } else if (Array.isArray(responseData?.service_providers?.data)) {
        providers = responseData.service_providers.data;
      }

      const mappedProviders = providers.filter(Boolean).map(provider => ({
        id: provider.id,
        name: provider.name,
        mobile: provider.mobile,
        email: provider.email,
        website: provider.website,
        description: provider.description,
        location: provider.location,
        location_id: provider.location_id,
        status: provider.status,
        facebook: provider.facebook,
        instagram: provider.instagram,
        twitter: provider.twitter,
        youtube: provider.youtube,
        wallpaper: Array.isArray(provider.wallpaper) ? provider.wallpaper[0] || null : provider.wallpaper,
        wallpaperAll: provider.wallpaper,
        categories: provider.categories,
        category_ids: provider.categories?.map(cat => cat?.id).filter(Boolean) || []
      }));

      return {
        category: {
          id: category?.id,
          name: category?.category || category?.name,
          description: category?.description,
          photo: category?.photo,
        },
        providers: mappedProviders,
        pagination: {
          current_page: responseData?.current_page || 1,
          per_page: responseData?.per_page || 25,
          total_records: responseData?.total_records || mappedProviders.length,
          total_pages: responseData?.total_pages || 1,
        }
      };
    } catch (error) {
      console.error(`Error fetching category details for ${categoryId}:`, error);
      return { category: null, providers: [], pagination: { current_page: 1, total_pages: 1 } };
    }
  },

  // Get service categories list (used for budget categories)
  getServiceCategories: async () => {
    try {
      const response = await apiClient.get('/service-categories');
      const categoriesArray = Array.isArray(response?.data?.data) ? response.data.data :
                              Array.isArray(response?.data) ? response.data :
                              Array.isArray(response) ? response : [];
      return categoriesArray;
    } catch (error) {
      console.error('Error fetching service categories:', error);
      return [];
    }
  },

  // Create new service provider
  createProvider: (data) => apiClient.post('/service-providers', data),

  // Update service provider
  updateProvider: (id, data) => apiClient.put(`/service-providers/${id}`, data),

  // Delete service provider
  deleteProvider: (id) => apiClient.delete(`/service-providers/${id}`),

  // Search providers (by name, location, category)
  searchProviders: (query) => apiClient.post('/sp-search', { query }),

  // Get provider profile (for logged-in provider)
  getProfile: () => apiClient.get('/service-provider/profile'),

  // Update provider profile
  updateProfile: (id, data) => apiClient.put(`/service-provider/profile/${id}`, data),

  // Get provider categories
  getMyCategories: () => apiClient.get('/service-provider/my-categories'),

  // Get provider dashboard stats
  getDashboard: () => apiClient.get('/service-provider/dashboard'),

  // Get SP inquiries
  getInquiries: () => apiClient.get('/sp/inquiries'),

  // Update inquiry status
  updateInquiryStatus: (id, status) => apiClient.put(`/sp/inquiries/${id}/status`, { status }),

  // Get SP reviews
  getReviews: () => apiClient.get('/sp/reviews'),

  // Upload portfolio image
  uploadPortfolioImage: (data) => apiClient.post('/sp/portfolio/images', data),

  // Delete portfolio image
  deletePortfolioImage: (id) => apiClient.delete(`/sp/portfolio/images/${id}`),
};

export default spApi;
